/*
  Automatic tier freebies
  ==================================================================
  Whichever cart-goal tier the shopper has reached, exactly that tier's set of
  products is present in the cart as free lines. Crossing upward adds them,
  crossing down removes them, moving sideways swaps them, and none of it
  reloads the page.

  Design rules, each of which exists because breaking it caused a real bug:

  1. THE TIER IS DERIVED FROM THE CART, NEVER FROM THE PAGE.
     Markup describes the cart as it stood when it rendered. Any update that
     does not re-render the drawer leaves it lying, and a stale tier means a
     freebie that should have been withdrawn quietly stays put.

  2. THE TIER IGNORES FREE LINES.
     If a freebie counted toward the total, adding one could cross the next
     tier, which adds another. Removing one could drop the cart below the tier
     that earned it, and the two states oscillate forever.

  3. NOTHING IS ASSUMED TO HAVE WORKED.
     Every pass re-reads /cart.js and reconciles against reality, so an
     interrupted or partially failed run self-corrects rather than compounding.

  4. A FAILURE IS NEVER RETRIED BLINDLY.
     An add that cannot succeed — out of stock, not in this sales channel —
     would otherwise fail, trigger a cart event, and fail again forever.
     Failures are remembered until the situation actually changes.
*/
(function () {
  'use strict';

  var SECTIONS = ['cart-count-badge', 'offcanvas-cart'];
  var MAX_PASSES = 5;      /* a reconcile that keeps finding work is a bug, not a workload */
  var DEBOUNCE_MS = 90;

  var running = false;
  var rerunRequested = false;

  /*
    Variant ids the cart refused, each remembered with the reason AND the cart
    it was refused against.

    Suppressing a retry forever was wrong: a gift that failed once could never
    come back without a page reload, even after the shopper changed the cart or
    stock returned. Suppressing nothing was also wrong: the failure fires a
    cart event, which re-runs this, which fails again, forever.

    Keying on the cart signature separates the two. Our own failed request does
    not change the cart, so the immediate re-run is suppressed and the loop
    cannot form. Anything the shopper actually does changes the signature, and
    the gift is tried again.
  */
  var refused = Object.create(null);
  var refusedTier = null;

  function cartSignature(cart) {
    return (cart.items || []).map(function (li) {
      return li.key + ':' + li.quantity;
    }).join('|');
  }

  /* ---------------------------------------------------------------- utils */

  function routes() {
    var r = (window.Shopify && window.Shopify.routes) || {};
    return {
      add: (r.cart_add_url || '/cart/add') + '.js',
      change: (r.cart_change_url || '/cart/change') + '.js',
      cart: (r.cart_url || '/cart') + '.js'
    };
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          /* `description` is Shopify's shopper-facing sentence; `message` is
             the short code. Prefer the sentence — it is the only thing that
             explains the failure to anyone reading it. */
          var err = new Error(data.description || data.message || ('Cart request failed (' + res.status + ')'));
          err.status = res.status;
          throw err;
        }
        return data;
      }, function () {
        throw new Error('Cart did not return a valid response (' + res.status + ')');
      });
    });
  }

  function getCart() {
    return fetch(routes().cart, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store'
    }).then(function (r) {
      if (!r.ok) throw new Error('Could not read the cart (' + r.status + ')');
      return r.json();
    });
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function readConfig() {
    var el = document.querySelector('[data-cart-freebie-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('[freebies] config is not valid JSON:', err);
      return null;
    }
  }

  /* ------------------------------------------------------------- notices */

  var stickyError = null;

  function notice(opts) {
    var host = document.querySelector('cart-container') || document.querySelector('.offcanvas-cart');
    if (!host) return;

    var el = host.querySelector('.cart-freebie-status');
    if (!opts) {
      if (el) el.remove();
      return;
    }
    if (!el) {
      el = document.createElement('div');
      host.insertBefore(el, host.firstChild);
    }

    var isError = opts.type === 'error';
    el.className = 'cart-freebie-status' + (isError ? ' cart-freebie-status--error' : '');
    el.setAttribute('role', isError ? 'alert' : 'status');
    el.setAttribute('aria-live', isError ? 'assertive' : 'polite');

    var html = isError
      ? '<span class="cart-freebie-status__icon" aria-hidden="true">!</span>'
      : '<span class="cart-freebie-status__spinner" aria-hidden="true"></span>';
    html += '<span class="cart-freebie-status__body">' + escapeHtml(opts.text);
    if (opts.details && opts.details.length) {
      html += '<ul class="cart-freebie-status__list">';
      opts.details.forEach(function (d) { html += '<li>' + escapeHtml(d) + '</li>'; });
      html += '</ul>';
    }
    html += '</span>';
    el.innerHTML = html;
  }

  function showError(text, details) {
    stickyError = { type: 'error', text: text, details: details || [] };
    notice(stickyError);
  }

  function clearError() {
    stickyError = null;
    notice(null);
  }

  /* An error must outlive the cart re-render that follows it. */
  function restoreNotice() {
    if (stickyError) notice(stickyError);
    else notice(null);
  }

  /* ---------------------------------------------------------------- tier */

  function computeTier(cfg, paidTotal, paidQty) {
    var th = cfg.thresholds;
    if (!th || !th.amount) return 0;

    var tier = 0;
    for (var i = 0; i < 3; i++) {
      var amount = th.amount[i];
      var qty = th.qty ? th.qty[i] : null;

      var amountMet = amount != null && paidTotal >= amount;
      var qtyMet = qty != null && paidQty >= qty;

      var met;
      if (th.metric === 'quantity') met = qtyMet;
      else if (th.metric === 'both') met = amountMet && qtyMet;
      else met = amountMet;

      /* Stop at the first tier not met. Tiers are meant to ascend; if they are
         ever configured out of order, awarding one the shopper skipped past
         would be worse than awarding none. */
      if (!met) break;
      tier = i + 1;
    }
    return tier;
  }

  /* --------------------------------------------------------- section swap */

  function applySections(sections) {
    if (!sections) return;
    if (window.updateAllCartElements) {
      window.updateAllCartElements(sections);
      return;
    }
    Object.keys(sections).forEach(function (key) {
      var doc = new DOMParser().parseFromString(sections[key], 'text/html');
      if (key === 'cart-count-badge') {
        document.querySelectorAll('.cart-count-badge').forEach(function (el) {
          var next = doc.querySelector('.cart-count-badge');
          if (next) el.replaceWith(next);
        });
      }
      if (key === 'offcanvas-cart') {
        document.querySelectorAll('cart-container').forEach(function (el) {
          var next = doc.querySelector('cart-container');
          if (next) el.replaceWith(next);
        });
      }
    });
  }

  /* ----------------------------------------------------------- one pass */

  function isFreebie(li, prop) {
    return !!(li.properties && li.properties[prop]);
  }

  /*
    Returns true when it changed the cart, meaning another pass should verify
    the result. Returns false when the cart already matches the target.
  */
  function pass(cfg) {
    var R = routes();
    var prop = cfg.property || '_free_gift';

    return getCart().then(function (cart) {
      var items = cart.items || [];
      var have = items.filter(function (li) { return isFreebie(li, prop); });
      var paid = items.filter(function (li) { return !isFreebie(li, prop); });

      var paidTotal = paid.reduce(function (s, li) { return s + li.final_line_price; }, 0);
      var paidQty = paid.reduce(function (s, li) { return s + li.quantity; }, 0);

      var tier = cfg.enabled ? computeTier(cfg, paidTotal, paidQty) : 0;

      var signature = cartSignature(cart);

      /* A different tier is a different question entirely. */
      if (tier !== refusedTier) {
        refused = Object.create(null);
        refusedTier = tier;
      }

      var entries = (cfg.tiers && cfg.tiers[String(tier)]) || [];
      var want = [];
      var blocked = [];
      entries.forEach(function (e) {
        if (!e) return;
        if (e.id && !e.blocked) want.push(e.id);
        else blocked.push((e.title || 'Free gift') + ' — ' + (e.blocked || 'Could not be added.'));
      });

      /*
        A cart holding nothing but free lines is a dead end: they are
        deliberately not removable, so the shopper cannot get back to an empty
        cart. This also covers the case where the feature was switched off
        while freebies were sitting in someone's cart.
      */
      if (!paid.length) want = [];

      /*
        Skip a gift only while the cart still looks exactly as it did when the
        request was refused. Once anything moves, it is worth another attempt.
      */
      var stillRefused = function (id) {
        return refused[id] && refused[id].signature === signature;
      };
      want.forEach(function (id) {
        if (stillRefused(id)) blocked.push(refused[id].message);
      });
      want = want.filter(function (id) { return !stillRefused(id); });

      var haveIds = have.map(function (li) { return li.variant_id; });
      var toAdd = want.filter(function (id) { return haveIds.indexOf(id) === -1; });

      var seen = Object.create(null);
      var toRemove = [];
      var toNormalise = [];
      have.forEach(function (li) {
        var wanted = want.indexOf(li.variant_id) !== -1;
        if (!wanted || seen[li.variant_id]) {
          toRemove.push(li);
        } else if (li.quantity !== 1) {
          /* Fix the quantity in place rather than removing and re-adding —
             one request instead of two, and no flicker of the line vanishing. */
          toNormalise.push(li);
        }
        seen[li.variant_id] = true;
      });

      var messages = blocked.slice();
      var work = toAdd.length || toRemove.length || toNormalise.length;

      if (!work) {
        if (messages.length) {
          showError('Your ' + ((cfg.tierTitles && cfg.tierTitles[String(tier)]) || 'reward') + ' could not be added:', messages);
        } else {
          clearError();
        }
        return false;
      }

      notice({ type: 'working', text: have.length ? cfg.updatingText : cfg.addingText });

      /*
        Removals go first and one at a time. Line keys are stable identifiers,
        so this is not about keys shifting — it is that /cart/change.js applies
        one mutation per request, and letting them overlap invites the server
        to reject the later ones against a cart that moved underneath them.
      */
      var chain = Promise.resolve();

      toRemove.forEach(function (li) {
        chain = chain.then(function () {
          return post(R.change, { id: li.key, quantity: 0 }).catch(function (err) {
            /* A line that is already gone is a success, not a failure. */
            if (err.status !== 404 && err.status !== 422) messages.push(err.message);
          });
        });
      });

      toNormalise.forEach(function (li) {
        chain = chain.then(function () {
          return post(R.change, { id: li.key, quantity: 1 }).catch(function (err) {
            messages.push(err.message);
          });
        });
      });

      /*
        Added ONE AT A TIME on purpose. /cart/add.js treats an `items` array as
        all-or-nothing, so a single out-of-stock gift would silently block
        every other gift in the same tier.
      */
      var props = {};
      props[prop] = cfg.badge || 'Free gift';

      toAdd.forEach(function (id) {
        chain = chain.then(function () {
          return post(R.add, { items: [{ id: id, quantity: 1, properties: props }] })
            .catch(function (err) {
              var entry = entries.filter(function (e) { return e && e.id === id; })[0];
              var label = (entry && entry.title) || 'Free gift';
              var msg = label + ' — ' + err.message;
              refused[id] = { message: msg, signature: signature };
              messages.push(msg);
            });
        });
      });

      /* One final request carries the re-rendered sections back. */
      return chain
        .then(function () {
          return post(R.change, { id: 0, quantity: 0, sections: SECTIONS, sections_url: location.pathname })
            .catch(function () { return null; });
        })
        .then(function (res) {
          if (res) {
            applySections(res.sections);
            /* Tell the rest of the theme the cart moved. Our own listener will
               hear this and run one more verification pass, which finds
               nothing to do and stops — cheap, and it guarantees the count
               badge and totals agree with what we just wrote. */
            window.dispatchEvent(new CustomEvent('kt.cart.reloaded'));
          }
          if (messages.length) {
            showError('Your ' + ((cfg.tierTitles && cfg.tierTitles[String(tier)]) || 'reward') + ' could not be added:', messages);
          } else {
            clearError();
          }
          restoreNotice();
          return true;
        });
    });
  }

  /* ------------------------------------------------------------ scheduler */

  function reconcile() {
    if (running) { rerunRequested = true; return; }

    var cfg = readConfig();
    if (!cfg) return;

    running = true;

    var passes = 0;
    function step() {
      passes += 1;
      if (passes > MAX_PASSES) {
        console.warn('[freebies] gave up after ' + MAX_PASSES + ' passes');
        return Promise.resolve();
      }
      return pass(cfg).then(function (changed) {
        return changed ? step() : null;
      });
    }

    step()
      .catch(function (err) {
        showError('We could not update your free gift:', [err.message]);
        console.error('[freebies]', err);
      })
      .then(function () {
        running = false;
        notice(stickyError || null);
        if (rerunRequested) {
          rerunRequested = false;
          schedule();
        }
      });
  }

  var timer = null;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, DEBOUNCE_MS);
  }

  ['kt.cart.added', 'kt.cart.changed', 'kt.cart.reloaded'].forEach(function (ev) {
    window.addEventListener(ev, schedule);
    document.addEventListener(ev, schedule);
  });

  /*
    Not every cart update announces itself — the variant-change control
    re-renders the drawer from the theme's own bundle with no event of ours.
    Watching for the drawer being replaced catches those. The guard is two
    cheap comparisons, so unrelated DOM churn costs nothing.
  */
  (function () {
    var lastContainer = null;
    var lastConfig = null;

    function check() {
      var container = document.querySelector('cart-container');
      var blob = document.querySelector('[data-cart-freebie-config]');
      var text = blob ? blob.textContent : null;
      if (container === lastContainer && text === lastConfig) return;
      lastContainer = container;
      lastConfig = text;
      if (container) schedule();
    }

    new MutationObserver(check).observe(document.body, { childList: true, subtree: true });
    check();
  })();

  /*
    Extra verification points. The complaint these answer is "it does not
    update until I refresh": a cart mutation that neither fires one of the
    events above nor replaces the drawer leaves both triggers silent. Rather
    than guess which control does that, verify at the moments when being wrong
    would actually be seen or would actually matter.

    Each of these costs one /cart.js read and, when nothing differs, stops
    there without touching the cart.
  */
  document.addEventListener('shown.bs.offcanvas', function (e) {
    /* The shopper is about to look at the cart — the one moment it must be right. */
    if (e.target && e.target.id === 'offcanvas-cart') schedule();
  });

  document.addEventListener('visibilitychange', function () {
    /* Back from another tab, where the cart may have been changed. */
    if (document.visibilityState === 'visible') schedule();
  });

  window.addEventListener('pageshow', function (e) {
    /* Restored from the back/forward cache, showing a cart from history. */
    if (e.persisted) schedule();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
